import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Check, Shield, Zap, X, ChevronRight } from 'lucide-react';
import AutoMode from '@mui/icons-material/AutoMode';
import EvStation from '@mui/icons-material/EvStation';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const sans = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

export default function L2ServiceAgreementModal({ open, onClose, onConfirmHaveCharger, onConfirmWantCharger, onDecline }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-0 rounded-2xl bg-[#f5f3ee]">
        {/* Header */}
        <div className="bg-[#2F424D] px-6 pt-6 pb-5 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#D4A62A]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white" style={serif}>Gratis elbil-fordele</h2>
              <p className="text-xs text-white/60" style={sans}>Inkluderet med din el-aftale – 0 kr./md.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Benefits */}
          <div className="space-y-3">
            {[
              { icon: <AutoMode sx={{ fontSize: 18, color: '#2F424D' }} />, title: 'Saving Mode', desc: 'Automatisk opladning i de billigste timer', color: 'bg-[#2F424D]/10' },
              { icon: <Zap className="w-4 h-4 text-[#D4A62A]" />, title: 'Laderabat', desc: '10 øre rabat pr. kWh via enkel app', color: 'bg-[#D4A62A]/10' },
              { icon: <Shield className="w-4 h-4 text-[#2F424D]" />, title: 'Serviceaftale', desc: 'Support og overvågning af ladeboks', color: 'bg-[#2F424D]/10' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-[#e8e4dc]">
                <div className={`w-9 h-9 rounded-lg ${b.color} flex items-center justify-center flex-shrink-0`}>{b.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#2F424D]" style={sans}>{b.title}</p>
                  <p className="text-xs text-gray-500" style={sans}>{b.desc}</p>
                </div>
                <Check className="w-4 h-4 text-[#2F424D] flex-shrink-0" />
              </div>
            ))}
          </div>

          {/* Compatible chargers explanation */}
          <div className="bg-white border border-[#e8e4dc] rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-[#2F424D] mb-1" style={sans}>Kompatible ladebokse</p>
            <p className="text-xs text-gray-500 leading-relaxed" style={sans}>
              Zaptec Go · Zaptec Go 2 · NexBlue Edge 2 · Easee-modeller. Har du en af disse, kan du aktivere alle fordele gratis.
            </p>
          </div>

          {/* Cancel existing service agreement note */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-xs font-bold text-amber-900 mb-1" style={sans}>Har du allerede en serviceaftale?</p>
            <p className="text-xs text-amber-800 leading-relaxed" style={sans}>
              Vi klarer alt med dit el-skift – men har du en eksisterende serviceaftale hos en anden udbyder, skal du selv opsige den. Resten tager vi os af!
            </p>
          </div>

          {/* Action buttons */}
          <div className="space-y-2.5">
            <button onClick={onConfirmHaveCharger}
              className="w-full flex items-center gap-3 bg-[#2F424D] hover:bg-[#1F2E33] text-white font-semibold py-3.5 px-4 rounded-xl transition-all hover:shadow-lg text-left group"
              style={sans}
            >
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-[#D4A62A]" />
              </div>
              <div className="flex-1">
                <span className="block text-sm font-bold">Jeg har kompatibel ladeboks</span>
                <span className="block text-[11px] text-white/60 mt-0.5">Aktivér gratis Saving Mode + Laderabat + Serviceaftale</span>
              </div>
              <ChevronRight className="w-4 h-4 text-white/40 group-hover:text-white/70 transition-colors flex-shrink-0" />
            </button>

            <button onClick={onConfirmWantCharger}
              className="w-full flex items-center gap-3 bg-[#D4A62A] hover:bg-[#c29625] text-[#2F424D] font-semibold py-3.5 px-4 rounded-xl transition-all hover:shadow-lg text-left group"
              style={sans}
            >
              <div className="w-9 h-9 rounded-lg bg-[#2F424D]/10 flex items-center justify-center flex-shrink-0">
                <EvStation sx={{ fontSize: 20, color: '#2F424D' }} />
              </div>
              <div className="flex-1">
                <span className="block text-sm font-bold">Jeg vil købe ladeboks</span>
                <span className="block text-[11px] text-[#2F424D]/60 mt-0.5">Vælg ladeboks + installation · Alt inkluderet</span>
              </div>
              <ChevronRight className="w-4 h-4 text-[#2F424D]/40 group-hover:text-[#2F424D]/70 transition-colors flex-shrink-0" />
            </button>

            <button onClick={onDecline}
              className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2 transition-colors"
              style={sans}
            >
              Ikke relevant nu
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}